<template>
  <UCard
    :class="[
      'mb-4 relative interview-kft ring-[1px] duration-700 transition-shadow shadow rounded-lg',
      ringColor,
      isRead ? 'opacity-80' : '',
    ]"
  >
    <div class="flex justify-between px-8 flex-col md:flex-row">
      <div class="flex justify-between">
        <div class="">
          <nuxt-link
            class="flex justify-center flex-col"
            :to="`/cau-hoi-phong-van/${question?.slug}`"
            no-prefetch
          >
            <InterviewTagHeader
              :rank="question.rank?.slug"
              :check-rounded="true"
              class="border top-[-1px] absolute hover:cursor-pointer"
            >
              {{ addLeadingZero(Number(index) + 1) }}
            </InterviewTagHeader>
            <div class="relative wrap-question mt-[42px] mb-3">
              <MarkdownPreview :model-value="question.question" :is-show-ads="false" />
              <div v-if="checkRankQuestion" :id="'blur-text-' + question.id" />
            </div>
          </nuxt-link>
          <UButton
            class="mb-6"
            variant="soft"
            color="neutral"
            size="sm"
            :rank="question.rank?.slug"
            @click="handleClick"
          >
            <span class="text-sm font-medium leading-5">
              {{ isActive ? LL.hide_answers() : LL.show_answer() }}
            </span>
            <UIcon
              :name="
                isActive
                  ? 'i-heroicons-arrow-up-20-solid'
                  : 'i-heroicons-arrow-right-20-solid'
              "
              class="ml-2.5"
            />
          </UButton>
        </div>
      </div>
      <div
        class="flex flex-row md:flex-col items-center justify-between md:justify-center hover:cursor-auto gap-2"
      >
        <InterviewRankInList
          class="hover:cursor-auto"
          :rank="question.rank?.slug"
          @click="handleClickExplainRank"
        />
        <UButton
          v-if="isRead"
          size="xs"
          color="success"
          variant="subtle"
          class="text-center"
        >
          <UIcon name="i-heroicons-check-20-solid" class="mr-1" />
          {{ LL.label_uncheck_interview() }}
        </UButton>
      </div>
    </div>
    <div
      v-if="isLoading"
      class="space-y-2 mt-4 bg-neutral-50 dark:bg-inherit py-2 px-4 rounded-lg"
    >
      <USkeleton class="h-4 w-full" />
      <USkeleton class="h-4 w-full" />
    </div>
    <div
      v-if="isActive"
      class="dark:bg-inherit pt-8 py-2 px-8 relative wrap-answer border-t border-gray-200 dark:border-neutral-800"
    >
      <div class="flex gap-2 items-center">
        <UCheckbox
          v-model="isRead"
          name="notifications"
          :label="isRead ? LL.label_uncheck_interview() : LL.label_check_interview()"
          :ui="{
            label: 'font-normal text-sm mt-px text-neutral-500',
            container: 'mb-2',
          }"
          @change="handleClickCheckBox"
        />
      </div>
      <MarkdownPreview :model-value="answer" :is-show-ads="false" />
      <nuxt-link
        class="flex justify-center flex-col mb-6 mt-3.5"
        :to="`/cau-hoi-phong-van/${question?.slug}`"
        no-prefetch
      >
        <InterviewButtonDetail
          class="z-50 hover:cursor-pointer flex justify-center items-center w-[150px]"
          :rank="question.rank?.slug"
        >
          {{ LL.view_details() }}
          <UIcon name="i-heroicons-arrow-right-20-solid" class="ml-2.5" />
        </InterviewButtonDetail>
      </nuxt-link>
      <div :id="'blur-answer-' + question.id" />
      <UButton
        v-if="!userStore.isLogged"
        :ui="{
          base: 'absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2',
        }"
        size="xs"
        color="secondary"
        variant="subtle"
        @click="authStore.setOpenAuthModal(true)"
        >{{ LL.you_need_to_login_to_view() }}
      </UButton>
      <UButton
        v-if="isShowUpgrade"
        size="xs"
        color="secondary"
        to="/nang-cap-tai-khoan"
        variant="subtle"
        :ui="{
          base: 'absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2',
        }"
        >{{ LL.you_need_to_upgrade_your_account_to_view() }}
      </UButton>
    </div>
  </UCard>
</template>
<script lang="ts" setup>
import { getAnswerBySlug, putMarkReadQuestion } from "features/question";
import Ranks from "@/constants/ranks";
import "md-editor-v3/lib/preview.css";
import { typesafeI18n } from "~/locales/i18n-vue";
import { useUserStore } from "~/store";
import { isHiddenBlurQuestionFunc } from "~/features/ranking/ranking.utils";
import { getRingColorInterviewItem } from "~/features/ranking";
import { useAuthStore } from "~/features/auth";

const props = defineProps({
  index: {
    type: Number,
    required: true,
  },
  question: {
    type: Object,
    required: true,
  },
  activeNames: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(["click", "open-explain-rank"]);
const answer = ref<string>("");
const isLoading = ref<boolean>(false);
const isGetAnswer = ref<boolean>(false);
const userStore = useUserStore();
const authStore = useAuthStore();
const toast = useToast();
const { LL } = typesafeI18n();
const isRead = ref(props.question.isMarkRead);

const isHiddenBlurQuestion = computed(() => {
  return isHiddenBlurQuestionFunc(userStore.roles);
});
const handleClickExplainRank = (rank: string) => {
  emit("open-explain-rank", rank);
};

const handleClick = async () => {
  if (!isGetAnswer.value) {
    isLoading.value = true;
    const data = await getAnswerBySlug(props.question?.slug);
    if (data) {
      answer.value = data?.answer;
      isGetAnswer.value = true;
    }
    isLoading.value = false;
  }
  emit("click", props.question);
  nextTick(() => {
    document.querySelectorAll("p").forEach(function (p) {
      p.innerHTML = p.innerHTML.replace(/\[\d+\]/g, function (match) {
        return '<span class="hidden">' + match + "</span>";
      });
    });
    if (isHiddenBlurQuestion.value) return;
    const elementAnswer = document.getElementById("blur-answer-" + props.question.id);
    if (
      elementAnswer &&
      ((elementAnswer && !userStore.isLogged) || isShowUpgrade.value)
    ) {
      const params: Partial<CSSStyleDeclaration> = {
        position: "absolute",
        width: "100%",
        bottom: "0",
        backdropFilter: "blur(5px)",
      };
      if (!userStore.isLogged || checkRankQuestion.value) {
        params.height = "70%";
      }
      Object.assign(elementAnswer.style, params);
    }
  });
};

const isActive = computed(() => {
  return !!props.activeNames.includes(props.question?.slug);
});

const checkRankQuestion = computed(() => {
  return (
    props.question.rank?.slug === Ranks.Middle ||
    props.question.rank?.slug === Ranks.Senior ||
    props.question.rank?.slug === Ranks.Expert
  );
});

const isShowUpgrade = computed(() => {
  return checkRankQuestion.value && userStore.roles.length === 1;
});

const addLeadingZero = (number) => {
  return number < 10 ? "0" + number : number;
};

const handleClickCheckBox = async () => {
  if (!userStore.isLogged) {
    toast.add({ title: LL.value.you_need_to_login_to_view(), color: "error" });
    authStore.setOpenAuthModal(true);
  } else {
    try {
      const newIsRead = isRead.value;
      await putMarkReadQuestion(props.question.id, newIsRead);
      isRead.value = newIsRead;
    } catch (error) {
      toast.add({ title: JSON.stringify(error), color: "error" });
    }
  }
};

onMounted(() => {
  if (isHiddenBlurQuestion.value) return;
  const element = document.getElementById("blur-text-" + props.question.id);
  if (element) {
    const params: Partial<CSSStyleDeclaration> = {
      position: "absolute",
      width: "30%",
      height: "100%",
      bottom: "0",
      right: "0",
      backdropFilter: "blur(5px)",
    };
    Object.assign(element.style, params);
  }
});

const ringColor = getRingColorInterviewItem(props.question.rank?.slug);
</script>
<style scoped>
.interview-kft .wrap-question {
  user-select: none;
}

.interview-kft .wrap-question:hover [id*="blur-text"] {
  backdrop-filter: unset !important;
}
</style>
